import { add, isAfter, isValid } from 'date-fns';
import { isEqual } from 'lodash-es';
import { minYear, parseISODate } from 'platform/forms-system/src/js/helpers';
import {
  convertToDateField,
  validateDate,
} from 'platform/forms-system/src/js/validation';
import { formatFileSize } from 'platform/forms-system/src/js/web-component-fields/vaFileInputFieldHelpers';
import { isValidDateRange } from 'platform/forms/validations';
import content from '../locales/en/content.json';
import { replaceStrValues } from './formatting';

const getDateParts = dateString => {
  if (!dateString || typeof dateString !== 'string') return null;
  const { day, month, year } = parseISODate(dateString);
  return [day, month, year];
};

const isValidDatePart = part => part && Number(part) !== 0;

const isCompleteDateString = dateString => {
  const parts = getDateParts(dateString);
  return parts && parts.every(isValidDatePart);
};

const hasAnyDateValue = dateString => {
  const parts = getDateParts(dateString);
  return parts && parts.some(isValidDatePart);
};

/**
 * Validates a text field for disallowed characters and adds an error message
 * when any invalid characters are found.
 *
 * @param {Object} errors - The rjsf/vets-forms error object
 * @param {string} fieldData - The input string to validate
 */
export const validateChars = (errors, fieldData) => {
  if (!fieldData || typeof fieldData !== 'string') return;

  const invalidCharsPattern = /[~!@#$%^&*+=[\]{}()<>;:"`\\/_|]/g;
  const matches = fieldData.match(invalidCharsPattern);

  if (!matches) return;

  const uniqueChars = [...new Set(matches)];
  const isPlural = uniqueChars.length > 1;
  const charsList = uniqueChars.join(' ');

  const msgPlural = content['validation--text-characters--plural'];
  const msgSingular = content['validation--text-characters--singular'];
  const message = isPlural
    ? `${msgPlural}: ${charsList}`
    : `${msgSingular}: ${charsList}`;

  errors.addError(message);
};

/**
 * Generic validator factory for date ranges with start/end date patterns
 *
 * Supports:
 * - Standard dates (YYYY-MM-DD)
 * - Partial month/year dates (YYYY, YYYY-MM)
 * @param {Object} options - configuration options
 * @param {string} options.startDateKey - key name for the start date field
 * @param {string} options.endDateKey - key name for the end date field
 * @param {boolean} [options.monthYear=false] - supports YYYY and YYYY-MM formats
 * @param {boolean} [options.allowEqualDates=false] - whether matching start and end dates are valid
 * @param {string} [options.invalidEndDateMessage] - custom error message for invalid end dates
 * @param {string} [options.rangeErrorMessage] - custom error message for invalid date range
 * @returns {Function} validator function that accepts (errors, data)
 */
export const validateDateRange = (options = {}) => {
  const {
    startDateKey,
    endDateKey,
    monthYear = false,
    allowEqualDates = false,
    invalidEndDateMessage = content['validation--date-value--current'],
    rangeErrorMessage = content['validation--date-range'],
  } = options;

  return (errors, data) => {
    const startDate = data[startDateKey];
    const endDate = data[endDateKey];

    if (monthYear) {
      const getComparableValue = (dateStr, defaultMonth) => {
        if (!dateStr) return null;

        const [yearValue, monthValue] = dateStr.split('-');
        const year = Number(yearValue);

        if (!Number.isInteger(year)) return null;

        const month =
          !monthValue || monthValue === 'XX'
            ? Number(defaultMonth)
            : Number(monthValue);

        if (!Number.isInteger(month)) return null;

        return year * 12 + month;
      };

      const start = getComparableValue(startDate, '01');
      const end = getComparableValue(endDate, '12');

      // Validate date range (end date must be after start date)
      if (start !== null && end !== null && start > end) {
        errors[endDateKey].addError(rangeErrorMessage);
      }

      return;
    }

    const fromDate = convertToDateField(startDate);
    const toDate = convertToDateField(endDate);

    // Validate end date is a valid date, if provided
    if (endDate && !isValid(new Date(endDate))) {
      errors[endDateKey].addError(invalidEndDateMessage);
      return;
    }

    // Validate the date order and optionally allow matching dates
    const datesAreEqual = allowEqualDates && isEqual(fromDate, toDate);
    if (!isValidDateRange(fromDate, toDate) && !datesAreEqual) {
      errors[endDateKey].addError(rangeErrorMessage);
    }
  };
};

/**
 * Validates that a date is not more than one year in the future.
 *
 * Ensures the date is complete with all three parts (day, month, year), valid, within the allowed year
 * range (minYear to current year + 1), and not more than one calendar year from today. Used for dates
 * that should be current or near-future.
 *
 * @param {Object} errors - The errors object to add validation errors to
 * @param {string} dateString - The date string to validate (format: 'YYYY-MM-DD')
 * @param {Object} formData - The complete form data object
 * @param {Object} schema - The JSON schema for the date field
 * @param {Object} [errorMessages={}] - Optional custom error messages to override defaults
 */
export const validateFutureDate = (
  errors,
  dateString,
  formData,
  schema,
  errorMessages = {},
) => {
  if (!hasAnyDateValue(dateString)) return;

  const maxYear = new Date().getFullYear() + 1;
  validateDate(
    errors,
    dateString,
    formData,
    schema,
    errorMessages,
    undefined,
    undefined,
    minYear,
    maxYear,
  );

  if (errors.__errors?.length > 0) return;

  const {
    invalidDate = content['validation--date-value--invalid'],
    futureDate = content['validation--date-range--future'],
  } = errorMessages;

  if (!isCompleteDateString(dateString)) {
    errors.addError(invalidDate);
    return;
  }

  const date = new Date(dateString);
  const yearFromToday = add(new Date(), { years: 1 });
  if (isValid(date) && isAfter(date, yearFromToday)) {
    errors.addError(futureDate);
  }
};

/**
 * Validates the combined size of all uploaded files does not exceed the configured maximum.
 *
 * @param {Object} options - Configuration options
 * @param {string} [options.arrayPath='attachments'] - Path to the array of uploaded files
 * @param {string} [options.fileField='attachmentFile'] - Field that receives the validation error
 * @param {number} [options.maxBytes=10485760] - Maximum allowed total file size in bytes
 * @returns {Function} Validator function that accepts (errors, data, fullData)
 */
export const validateTotalFileSize = (options = {}) => {
  const {
    arrayPath = 'attachments',
    fileField = 'attachmentFile',
    maxBytes = 1024 * 1024 * 10,
  } = options;

  return (errors, _, fullData) => {
    const files = fullData?.[arrayPath];
    if (!Array.isArray(files)) return;

    const totalBytes = files.reduce(
      (total, item) =>
        total +
        (Number.isFinite(item?.[fileField]?.size) ? item[fileField].size : 0),
      0,
    );

    if (totalBytes > maxBytes) {
      const msg = replaceStrValues(
        content['validation--total-file-size'],
        formatFileSize(maxBytes),
      );
      errors[fileField].addError(msg);
    }
  };
};
